import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Spinner } from 'admin-components/components';

import { Row } from 'components/shared/Layout';
import Tabs from 'components/ui/TabsRefund';
import FormattedCurrency from 'components/ui/FormattedCurrency';

import { paymentsShape, userShape, bookingShape } from 'constants/propTypes';

import PaymentMethod from 'pages/BookingDetails/Payments/PaymentMethod';
import ReviewBookingSummary from 'components/ui/ReviewBookingSummary';
import { GET_BOOKING } from 'store/queries';
import { useManagedQuery } from 'utils/managedHooks';
import { isUserThalysAgent } from 'utils/booking';
import {
  getAvailableRefundMethods,
  getRefundTabsForBooking,
  findSuccessRefundsMadeAgainstRefId,
  getRefundRefId,
  calcTotalToBeRefunded,
  getRefundAmount,
  findS3Refunds
} from './Payments.utils';

import {
  PaymentSummary,
  PaymentContainer,
  PaymentSelectionContainer,
  StyledPaymentContainer,
  StyledAmount,
  StyledPayments,
  StyledSpacedApart,
  StyledAmountDue,
  Divider
} from './Payments.styles';

import { handleRefundSubmit } from './SplitPayment/SplitPayment.utils';

import RefundComponents from './RefundComponents';

const CancelVoucherRefunds = ({
  availableMethods,
  processCardPaymentRefund,
  addPayment,
  addPayoutAndBalance,
  updatePayments,
  loading,
  currency,
  reference,
  customer,
  payments,
  refunds,
  user,
  booking
}) => {
  const [selectedPaymentMethod, changePaymentMethod] = useState(null);
  const getAssociatedBooking = booking.extraOnlyParentBookingReference;

  const { data, loading: getBookingLoading } = useManagedQuery(GET_BOOKING, {
    variables: { reference: getAssociatedBooking }
  });

  if (getBookingLoading) {
    return <Spinner />;
  }

  const isThalysAgent = isUserThalysAgent(user);
  const refundMethods = getAvailableRefundMethods({ availableMethods, payments: data.booking.payments, isThalysAgent });
  const tabs = getRefundTabsForBooking(refundMethods);
  const paymentMethod = selectedPaymentMethod || tabs[0].value;

  const refId = getRefundRefId(payments);
  const refundAmount = getRefundAmount(payments);
  const partialRefunds = findSuccessRefundsMadeAgainstRefId(refId)(refunds);
  const toBeRefunded = calcTotalToBeRefunded(payments, refunds, refId);
  const isOverpaid = payments?.find((p) => p.refId === refId)?.reference?.endsWith('_overpaid') ?? false;

  if (toBeRefunded <= 0 || findS3Refunds(payments).length < 1) {
    return <Redirect to={{ pathname: `/manage/bookings/${reference}/journeys` }} />;
  }

  return (
    <ReviewBookingSummary title="payments.makeRefund">
      <Row>
        <PaymentSelectionContainer alignItems="center">
          <h2>
            <FormattedMessage id="payments.refundTypes" />
          </h2>
          <Tabs
            payments={data.booking.payments}
            currency={currency}
            onClick={changePaymentMethod}
            selectedValue={paymentMethod}
            tabs={tabs}
          />
        </PaymentSelectionContainer>

        <PaymentContainer columns={12}>
          <PaymentSummary span={5}>
            <StyledAmount data-cy="amount-due">
              <h3>
                <FormattedMessage id="payments.refundAmount" />
              </h3>
              <StyledAmountDue>
                <FormattedCurrency amount={refundAmount} currency={currency} />
              </StyledAmountDue>
              <h4>
                <FormattedMessage id="refunds.title" />
              </h4>
            </StyledAmount>
            <StyledPayments data-cy="provisional-payments">
              {partialRefunds.map((refund) => (
                <li key={refund.id}>
                  <StyledSpacedApart>
                    <PaymentMethod method={refund.method} />
                    <Divider />
                    <FormattedCurrency amount={refund.amount} currency={currency} />
                  </StyledSpacedApart>
                </li>
              ))}
            </StyledPayments>
            <StyledSpacedApart data-cy="remaining-refund">
              <FormattedMessage id="payments.totalOutstanding" />
              <Divider />
              <FormattedCurrency amount={toBeRefunded} currency={currency} />
            </StyledSpacedApart>
          </PaymentSummary>

          <StyledPaymentContainer span={7}>
            <RefundComponents
              handleRefundSubmit={handleRefundSubmit}
              addPayment={addPayment}
              addPayoutAndBalance={addPayoutAndBalance}
              bookingReference={reference}
              currency={currency}
              customer={customer}
              loading={loading}
              method={paymentMethod.substring(paymentMethod.indexOf('_') + 1)}
              reference={refId}
              totalPriceToBePaid={toBeRefunded}
              updatePayments={updatePayments}
              processCardPaymentRefund={processCardPaymentRefund}
              paymentLocation={user.paymentLocation}
              payments={data.booking.payments}
              isPackage={data.booking.isPackage}
              hotels={data.booking.hotels}
              isCancelVoucher
              mainBookingRef={getAssociatedBooking}
              isOverpaid={isOverpaid}
            />
          </StyledPaymentContainer>
        </PaymentContainer>
      </Row>
    </ReviewBookingSummary>
  );
};

CancelVoucherRefunds.propTypes = {
  addPayment: PropTypes.func,
  addPayoutAndBalance: PropTypes.func,
  availableMethods: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string
    })
  ),
  customer: PropTypes.shape({
    email: PropTypes.string
  }),
  currency: PropTypes.string,
  loading: PropTypes.bool,
  payments: paymentsShape,
  processCardPaymentRefund: PropTypes.func,
  reference: PropTypes.string,
  refunds: paymentsShape,
  updatePayments: PropTypes.func,
  user: userShape,
  booking: bookingShape
};

export default CancelVoucherRefunds;
